// @flow
import React, { useEffect, useRef, useState } from 'react';
import { Input, Box, Flex, Loader, Text, useToast } from '@locus-taxy/neo-ui';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import sendbirdSelectors from '@sendbird/uikit-react/sendbirdSelectors';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import useContactList from 'common/modules/chat/hooks/useContactList';
import Contact from './Contact';
import testIds from 'common/modules/chat/tests/chat.json';
import { ChatActions } from 'common/modules/chat/redux/actions/chat';

type Props = {
    setSelectedProfile: any,
    openMessageWindow: () => void
};

const Contacts = ({
    openMessageWindow,
    setSelectedProfile
}: Props): React$Element<React$FragmentType> => {
    const pageNumberRef = useRef(1);
    const [contacts, setContacts] = useState([]);
    const [isDataFinishedAtBE, setIsDataFinishedAtBE] = useState(false);
    const searchQuery = useRef('');
    const { t } = useTranslation('liveView');
    const { showToast } = useToast();
    const dispatch = useDispatch();

    const chatCxt = useSendbirdStateContext();
    const createChannel = sendbirdSelectors.getCreateGroupChannel(chatCxt);
    const sdk = sendbirdSelectors.getSdk(chatCxt);

    const { data, isLoading, error, refetch } = useContactList({
        query: searchQuery.current,
        page: pageNumberRef.current
    });

    const loadData = () => {
        pageNumberRef.current += 1;
        refetch();
    };

    const [sentryRef] = useInfiniteScroll({
        loading: isLoading,
        hasNextPage: !isDataFinishedAtBE,
        onLoadMore: loadData,
        disabled: !!error
    });

    const setDefaultsForContactsResults = () => {
        setContacts([]);
        pageNumberRef.current = 1;
    };

    const onSearch = useDebouncedCallback(value => {
        setDefaultsForContactsResults();
        searchQuery.current = value;
        refetch();
    }, 300);

    const handleContactOnClick = ({ userId, name, riderId, photoUrl }) => {
        dispatch(
            ChatActions.setUserProfile({ userId, name, riderId, photoUrl })
        );
        const params = {};
        const currentUserId = sdk.currentUser?.userId;

        params.isPublic = false;
        params.isEphemeral = false;
        params.isDistinct = true;
        params.invitedUserIds = [userId, currentUserId];

        createChannel(params)
            .then(channel => {
                /* Channel gets created irrespective of the userIds are present in sendbird DB or not.
                 * That's why checking if the userId is present in the memberlist in response */
                const [otherUser] = channel.members.filter(
                    member => member.userId === userId
                );
                if (otherUser) {
                    setSelectedProfile(channel.url);
                    openMessageWindow();
                } else {
                    showToast({
                        title: t('riderManagerChat.userNotFound', {
                            userId
                        }),
                        palette: 'danger',
                        anchorOrigin: { horizontal: 'left', vertical: 'bottom' }
                    });
                }
            })
            .catch(e => {
                showToast({
                    title: t('riderManagerChat.channelCreationError', {
                        userId
                    }),
                    content: e.message,
                    palette: 'danger',
                    anchorOrigin: { horizontal: 'left', vertical: 'bottom' }
                });
            });
    };

    useEffect(() => {
        if (data) {
            const isResponseEmpty = data.contacts.length === 0;
            setIsDataFinishedAtBE(isResponseEmpty);

            return setContacts(currentContacts => [
                ...currentContacts,
                ...data.contacts
            ]);
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            return setIsDataFinishedAtBE(true);
        }
    }, [error]);

    const isSearchResultEmpty =
        !isLoading && contacts.length === 0 && searchQuery.current.length > 0;

    return (
        <>
            <Box p={2}>
                <Input
                    placeholder={t('riderManagerChat.contactSearchMessage')}
                    startIcon="search"
                    fullWidth
                    onChange={event => onSearch(event.target.value)}
                    data-test-id={testIds.chatSearchInput}
                />
            </Box>
            <Flex
                flexDirection="column"
                flex={1}
                backgroundColor="white"
                minHeight="0"
                overflowY="auto"
                sx={{
                    '&::-webkit-scrollbar': {
                        width: '4px'
                    }
                }}
            >
                {contacts.length > 0
                    ? contacts.map(contact => (
                          <Contact
                              {...contact}
                              key={contact.userId}
                              onContactClick={handleContactOnClick}
                          />
                      ))
                    : !isLoading &&
                      searchQuery.current.length === 0 && (
                          <Box mt={3} sx={{ alignSelf: 'center' }}>
                              <Text color="grey.500" fontSize={2} mt={3}>
                                  {t('riderManagerChat.noContact')}
                              </Text>
                          </Box>
                      )}
                {!isLoading && <Box mb={2} ref={sentryRef} />}
                {!isDataFinishedAtBE && (
                    <Flex justifyContent="center" paddingTop={4} flex={1}>
                        <Loader color="blue.500" />
                    </Flex>
                )}
                {isSearchResultEmpty && (
                    <Flex
                        px={8}
                        flexGrow={1}
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="column"
                    >
                        <Box mb={4}>
                            <img src="img/contacts-search-results-empty.svg" />
                        </Box>
                        <Text color="grey.500" fontSize={2}>
                            {t('riderManagerChat.contactSearchResultsEmpty')}
                        </Text>
                    </Flex>
                )}
            </Flex>
        </>
    );
};

export default Contacts;
